<template>
    <div class="automation-employee-meetings">
        <multiselect
            :value="selectedOptions"
            :options="monthOptions"
            :close-on-select="false"
            :clear-on-select="false"
            :placeholder="trans('pick_employees')"
            :allow-empty="true"
            :multiple="true"
            deselect-label=""
            select-label=""
            selected-label=""
            label="name"
            track-by="id"
            id="automation-input-employees"
            class="automation-employee-meetings__select"
            @select="addEmployee"
            @remove="removeEmployee"
        >
            <template slot="tag" slot-scope="props">
                <span class="automation-employee-meetings__tag">
                    <b>{{ props.option.name }}</b>
                    <span class="badge badge-warning contract-type" v-b-tooltip.hover.top :title="props.option.contract_type_full">{{ props.option.contract_type }}</span>
                    <i class="fal fa-times automation-employee-meetings__tag-remove" @mousedown.prevent.stop="props.remove(props.option)"></i>
                </span>
            </template>
            <template slot="option" slot-scope="props">
                <span class="automation-employee-meetings__option">
                    <span class="automation-employee-meetings__check" :class="{'is-checked': isSelected(props.option)}"><i class="fas fa-check"></i></span>
                    <span class="automation-change__avatar">{{ initials(props.option) }}</span>
                    <span class="automation-employee-meetings__option-name">{{ props.option.name }}</span>
                    <span class="badge badge-warning contract-type" :title="props.option.contract_type_full">{{ props.option.contract_type }}</span>
                    <span class="automation-employee-meetings__option-rate" v-if="hourlySalary(props.option)">{{ formatRate(hourlySalary(props.option)) }}</span>
                </span>
            </template>
            <span slot="noResult">{{ trans('no_employees') }}</span>
            <span slot="noOptions">{{ trans('no_employees') }}</span>
        </multiselect>
        <div class="invalid-feedback d-block" v-if="error('employees')">{{ error('employees') }}</div>

        <div class="automation-employee-meetings__employee" v-for="(employee, employeeIndex) in mutableValue" :key="employee.employee_contract_id">
            <div class="automation-employee-meetings__header">
                <span class="automation-change__avatar">{{ initials(option(employee.employee_contract_id)) }}</span>
                <div class="automation-employee-meetings__identity">
                    <b class="automation-employee-meetings__name">{{ option(employee.employee_contract_id) ? option(employee.employee_contract_id).name : '#' + employee.employee_contract_id }}</b>
                    <template v-if="option(employee.employee_contract_id)">
                        <span class="badge badge-warning contract-type" v-b-tooltip.hover.top :title="option(employee.employee_contract_id).contract_type_full">{{ option(employee.employee_contract_id).contract_type }}</span>
                        <span class="automation-employee-meetings__rate" v-if="hourlySalary(option(employee.employee_contract_id))">
                            {{ trans('rate') }}: {{ formatRate(hourlySalary(option(employee.employee_contract_id))) }}
                        </span>
                    </template>
                </div>
                <div class="automation-employee-meetings__summary">
                    <span class="automation-employee-meetings__count">{{ summaryLabel([employee]) }}</span>
                    <b class="automation-employee-meetings__total">{{ formatAmount(employeeAmount(employee)) }}</b>
                </div>
                <button type="button" class="btn automation-employee-meetings__copy" v-if="mutableValue.length > 1" @click="copyMeetingsToOthers(employee)">
                    <i class="far fa-clone"></i> {{ trans('copy_to_others') }}
                </button>
            </div>
            <div class="invalid-feedback d-block px-3" v-if="error('employees.' + employeeIndex + '.employee_contract_id')">{{ error('employees.' + employeeIndex + '.employee_contract_id') }}</div>

            <div class="automation-employee-meetings__body">
                <div class="automation-employee-meetings__meeting automation-employee-meetings__columns">
                    <span class="automation-employee-meetings__title">{{ trans('meeting_title') }}</span>
                    <span class="automation-employee-meetings__hours">{{ trans('meeting_hours') }}</span>
                    <span class="automation-employee-meetings__amount">{{ trans('meeting_amount') }}</span>
                    <span class="automation-employee-meetings__remove"></span>
                </div>

                <div class="automation-employee-meetings__meeting" v-for="(meeting, meetingIndex) in employee.meetings" :key="meetingIndex">
                    <div class="automation-employee-meetings__title">
                        <input type="text"
                            class="form-control"
                            :class="{'is-invalid': error(meetingKey(employeeIndex, meetingIndex, 'title'))}"
                            :placeholder="trans('meeting_title_placeholder')"
                            v-model="meeting.title">
                        <div class="invalid-feedback d-block" v-if="error(meetingKey(employeeIndex, meetingIndex, 'title'))">{{ error(meetingKey(employeeIndex, meetingIndex, 'title')) }}</div>
                    </div>
                    <div class="automation-employee-meetings__hours">
                        <div class="input-group">
                            <input type="number"
                                class="form-control"
                                min="0"
                                step="0.25"
                                placeholder="0"
                                :class="{'is-invalid': error(meetingKey(employeeIndex, meetingIndex, 'hours'))}"
                                v-model="meeting.hours">
                            <div class="input-group-append">
                                <span class="input-group-text">h</span>
                            </div>
                        </div>
                        <div class="invalid-feedback d-block" v-if="error(meetingKey(employeeIndex, meetingIndex, 'hours'))">{{ error(meetingKey(employeeIndex, meetingIndex, 'hours')) }}</div>
                    </div>
                    <span class="automation-employee-meetings__amount">{{ meetingAmount(employee, meeting) === null ? '—' : formatAmount(meetingAmount(employee, meeting)) }}</span>
                    <span class="automation-employee-meetings__remove">
                        <button type="button" class="btn btn-sm btn-link" :title="trans('remove_meeting')" @click="removeMeeting(employee, meetingIndex)" v-if="employee.meetings.length > 1">
                            <i class="far fa-trash-alt"></i>
                        </button>
                    </span>
                </div>
                <div class="invalid-feedback d-block" v-if="error('employees.' + employeeIndex + '.meetings')">{{ error('employees.' + employeeIndex + '.meetings') }}</div>

                <button type="button" class="btn btn-sm btn-link automation-employee-meetings__add" @click="addMeeting(employee)">
                    <i class="far fa-plus"></i> {{ trans('add_meeting') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import {cloneDeep} from 'lodash';
    import TranslationsUtils from "../app-components/TranslationsUtils";
    import {formatMoney} from "../utils";

    export default {
        name: 'automation-employee-meetings',
        mixins: [TranslationsUtils],
        props: {
            // [{employee_contract_id, meetings: [{title, hours}]}]
            value: { type: Array },
            // the contracts to choose from: [{id, name, contract_type, contract_type_full, date_start, date_end, hourly_salaries: {"Y-m": rate}}]
            options: { type: Array },
            // the chosen month "Y-m", only the contracts valid in it are offered
            month: { type: String },
            errors: { type: Object }
        },

        data() {
            return {
                mutableValue: cloneDeep(this.value || [])
            };
        },

        computed: {
            monthOptions() {
                if (!this.month) {
                    return this.options;
                }

                const start = this.month + '-01';
                const [year, month] = this.month.split('-').map(part => parseInt(part));
                const end = this.month + '-' + new Date(year, month, 0).getDate();

                // the dates are "Y-m-d", so they compare as strings
                return this.options.filter(option => option.date_start <= end && (!option.date_end || option.date_end >= start));
            },

            selectedOptions() {
                return this.mutableValue.map(employee => this.option(employee.employee_contract_id)).filter(option => option);
            },

            // the meetings of all the employees for the footer of the wizard, the preview calculates the amounts again
            summary() {
                if (!this.mutableValue.some(employee => employee.meetings.some(meeting => this.hours(meeting) !== null))) {
                    return null;
                }

                return {
                    label: this.summaryLabel(this.mutableValue),
                    amount: this.formatAmount(this.mutableValue.reduce((sum, employee) => sum + this.employeeAmount(employee), 0))
                };
            }
        },

        watch: {
            mutableValue: {
                deep: true,
                handler(value) {
                    this.$emit('input', cloneDeep(value));
                }
            },

            summary: {
                immediate: true,
                handler(summary) {
                    this.$emit('summary', summary);
                }
            }
        },

        methods: {
            addEmployee(option) {
                this.mutableValue.push({ employee_contract_id: option.id, meetings: [this.emptyMeeting()] });
            },

            removeEmployee(option) {
                this.mutableValue = this.mutableValue.filter(employee => employee.employee_contract_id !== option.id);
            },

            addMeeting(employee) {
                employee.meetings.push(this.emptyMeeting());
            },

            removeMeeting(employee, index) {
                employee.meetings.splice(index, 1);
            },

            // the meetings are mostly the same for all the employees, the hours can be changed after copying
            copyMeetingsToOthers(source) {
                this.mutableValue
                    .filter(employee => employee !== source)
                    .forEach(employee => { employee.meetings = cloneDeep(source.meetings); });
            },

            emptyMeeting() {
                return { title: '', hours: '' };
            },

            option(id) {
                return this.options.find(option => option.id === id);
            },

            isSelected(option) {
                return this.mutableValue.some(employee => employee.employee_contract_id === option.id);
            },

            initials(option) {
                if (!option) {
                    return '?';
                }

                return option.name.split(/\s+/).filter(part => part).slice(0, 2).map(part => part[0].toUpperCase()).join('');
            },

            hourlySalary(option) {
                return option && option.hourly_salaries && this.month ? (option.hourly_salaries[this.month] || null) : null;
            },

            hours(meeting) {
                const hours = parseFloat(meeting.hours);
                return isNaN(hours) || hours <= 0 ? null : hours;
            },

            // the estimate of the amount: the hours × the hourly salary of the contract in the month, rounded like the preview
            meetingAmount(employee, meeting) {
                const hourlySalary = this.hourlySalary(this.option(employee.employee_contract_id));
                const hours = this.hours(meeting);

                return hourlySalary === null || hours === null ? null : Math.round(hours * hourlySalary * 100) / 100;
            },

            employeeAmount(employee) {
                return employee.meetings.reduce((sum, meeting) => sum + (this.meetingAmount(employee, meeting) || 0), 0);
            },

            // "2 meetingy · 3,5 h", only the meetings with the hours are counted
            summaryLabel(employees) {
                const meetings = [].concat(...employees.map(employee => employee.meetings)).filter(meeting => this.hours(meeting) !== null);
                if (!meetings.length) {
                    return this.trans('no_meetings');
                }

                const count = meetings.length;
                const key = count === 1 ? 'meetings_one' : (count < 5 ? 'meetings_few' : 'meetings_many');
                const hours = meetings.reduce((sum, meeting) => sum + this.hours(meeting), 0);

                return this.trans(key).replace(':count', count) + ' · ' + formatMoney(hours, 'h', 0, 2);
            },

            formatAmount(amount) {
                return formatMoney(amount, '€');
            },

            formatRate(rate) {
                return formatMoney(rate, '€') + '/h';
            },

            meetingKey(employeeIndex, meetingIndex, attribute) {
                return 'employees.' + employeeIndex + '.meetings.' + meetingIndex + '.' + attribute;
            },

            error(key) {
                return this.errors && this.errors[key] ? this.errors[key][0] : null;
            }
        }
    };
</script>



// WEBPACK FOOTER //
// resources/assets/admin/js/components/AutomationEmployeeMeetings.vue